import { ethers } from 'ethers';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAccount } from 'wagmi';
import { CPGABI } from '../../ABI/ABI';
import BackendWalletSignInPanel from '../../components/auth/BackendWalletSignInPanel';
import ConnectWalletButton from '../../components/connectWallet/ConnectWalletButton';
import SwitchNetworkButton from '../../components/connectWallet/SwitchNetworkButton';
import { useBackendWalletAuth } from '../../hooks/useBackendWalletAuth';
import {
    targetChainId,
    CPGAddress,
    initialTokenList,
} from '../../constant/constant';
import {
    copyIcon,
    receiveIcon,
} from '../../constant/icon';
import DisputeModal from '../../Modal/DisputeModal';
import {
    copyToClipboard,
    formatNumber,
    shortenWalletAddress,
} from '../../utils/commonFunction';
import AmountDisplay from '../../components/amount/AmountDisplay';
import { readContractData } from '../../utils/contractInstance';
import {
    fetchLockedPaymentRequests,
    fetchPaymentRequests,
} from '../../utils/cpgDiamondReader';
import { getPaymentRequestMeta, sortPaymentRequestsNewestFirst } from '../../utils/paymentRequestNotes';
import SendRequest from './SendRequest';

const TABS = [
    { id: 'direct', label: 'Direct' },
    { id: 'milestone', label: 'Milestone' },
];

const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    try {
        const d = new Date(Number(timestamp));
        if (Number.isNaN(d.getTime())) return '—';
        return d.toISOString().slice(0, 10);
    } catch (_e) {
        return '—';
    }
};

const StatusPill = ({ accepted }) =>
    accepted ? (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800 dark:border-teal-900/50 dark:bg-teal-950/50 dark:text-teal-300">
            <i className="fa-solid fa-circle-check text-[10px]" />
            Accepted
        </span>
    ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300">
            <i className="fa-regular fa-clock text-[10px]" />
            Pending
        </span>
    );

const RequestRow = ({ row, isMilestone }) => {
    const idLabel = `REQ-${String(row.id).padStart(3, '0')}`;
    return (
        <div className="grid grid-cols-12 items-center gap-3 border-b border-zinc-200 px-5 py-4 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900/40">
            <div className="col-span-2 sm:col-span-2">
                <span className="font-mono text-xs font-semibold tracking-wide text-teal-700 dark:text-teal-400">
                    {idLabel}
                </span>
            </div>
            <div className="col-span-3 sm:col-span-2 flex items-center gap-2">
                <span className="font-mono text-sm text-zinc-800 dark:text-zinc-200">
                    {shortenWalletAddress(row.receiver)}
                </span>
                <button
                    type="button"
                    onClick={() => copyToClipboard(row.receiver)}
                    className="text-zinc-500 transition-colors hover:text-teal-700 dark:text-zinc-500 dark:hover:text-teal-400"
                    aria-label="Copy receiver address"
                >
                    <i className={`${copyIcon} text-xs`} />
                </button>
            </div>
            <div className="col-span-2 sm:col-span-2">
                <AmountDisplay
                    value={row.parsedNumber ?? row.amount}
                    className="font-bold text-zinc-900 dark:text-white"
                    suffix={row.tokenName}
                />
                {isMilestone && (
                    <div className="mt-0.5 text-[11px] text-zinc-600 dark:text-zinc-500">
                        {row.milestones} milestones
                    </div>
                )}
            </div>
            <div className="col-span-3 sm:col-span-3 truncate text-sm text-zinc-700 dark:text-zinc-300">
                {row.description || <span className="text-zinc-400 dark:text-zinc-600">—</span>}
            </div>
            <div className="hidden font-mono text-sm text-zinc-600 sm:col-span-2 sm:block dark:text-zinc-400">
                {row.dateLabel}
            </div>
            <div className="col-span-2 sm:col-span-1 flex justify-end">
                <StatusPill accepted={row.isAccepted} />
            </div>
        </div>
    );
};

const TableSkeleton = ({ rows = 3 }) => (
    <div>
        {Array.from({ length: rows }).map((_, i) => (
            <div
                key={i}
                className="grid grid-cols-12 items-center gap-3 border-b border-zinc-200 px-5 py-4 dark:border-zinc-800"
            >
                <div className="col-span-2 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-3 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="hidden h-3 animate-pulse rounded bg-zinc-200 sm:col-span-2 sm:block dark:bg-zinc-800" />
                <div className="col-span-1 h-5 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800" />
            </div>
        ))}
    </div>
);

const EmptyState = ({ tab, onCreate }) => (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-teal-200 bg-teal-50 dark:border-teal-900/50 dark:bg-teal-950/40">
            <i className="fa-solid fa-inbox text-teal-700 dark:text-teal-400" />
        </div>
        <h4 className="font-semibold text-zinc-900 dark:text-zinc-200">
            No {tab === 'milestone' ? 'milestone' : 'direct'} requests yet
        </h4>
        <p className="mt-1 max-w-sm text-sm text-zinc-600 dark:text-zinc-500">
            Create your first payment request and we&apos;ll show its on-chain status here.
        </p>
        <button
            type="button"
            onClick={onCreate}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
        >
            <i className="fa-solid fa-plus text-xs" />
            New Request
        </button>
    </div>
);

const GetPaid = () => {
    const [currentItem, setCurrentItem] = useState({});
    const [disputeModal, setDisputeModal] = useState(false);

    const [directPaymentRequestData, setDirectPaymentRequestData] = useState([]);
    const [trackedPaymentRequestData, setTrackedPaymentRequestData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [activeTab, setActiveTab] = useState('direct');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);

    const { isConnected, address: walletAddress, chainId } = useAccount();
    const {
        status: authStatus,
        error: authError,
        signIn,
        isReady: isBackendReady,
    } = useBackendWalletAuth({
        walletAddress,
        enabled: isConnected && chainId === targetChainId,
    });
    const formAnchorRef = useRef(null);

    // ---------- Data fetchers ----------
    const decorateRequest = (data, kind, chainIndex) => {
        const token = initialTokenList.find(
            (t) => t.address === data.tokenAddress,
        );
        if (!token) return null;
        const id = Number(data.requestId);
        const meta = getPaymentRequestMeta({
            chainId,
            sender: walletAddress,
            requestId: id,
            kind,
        });
        const parsedNumber = parseFloat(
            ethers.formatUnits(data.amount, token.decimals),
        );
        return {
            ...data,
            id,
            chainIndex,
            parsedNumber,
            amount: formatNumber(parsedNumber),
            tokenName: token.name,
            tokenImage: token.logoURI,
            description: meta.description || '',
            dateLabel: formatDate(meta.createdAt),
            ...(kind === 'milestone'
                ? { milestones: Number(data.milestones) }
                : {}),
        };
    };

    const getDirectPaymentRequestData = async () => {
        try {
            setLoading(true);
            const rows = await fetchPaymentRequests(
                readContractData,
                CPGAddress,
                CPGABI,
                walletAddress,
                'sent',
            );
            const requests = rows
                .map((data) => decorateRequest(data, 'direct', data.chainIndex))
                .filter(Boolean);
            setDirectPaymentRequestData(
                sortPaymentRequestsNewestFirst(requests, {
                    chainId,
                    sender: walletAddress,
                    kind: 'direct',
                }),
            );
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const getLockedPaymentRequestData = async () => {
        try {
            setLoading(true);
            const rows = await fetchLockedPaymentRequests(
                readContractData,
                CPGAddress,
                CPGABI,
                walletAddress,
                'sent',
            );
            const requests = rows
                .map((data) =>
                    decorateRequest(data, 'milestone', data.chainIndex),
                )
                .filter(Boolean);
            setTrackedPaymentRequestData(
                sortPaymentRequestsNewestFirst(requests, {
                    chainId,
                    sender: walletAddress,
                    kind: 'milestone',
                }),
            );
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isConnected && chainId === targetChainId) {
            getDirectPaymentRequestData();
            getLockedPaymentRequestData();
        }
        // eslint-disable-next-line
    }, [walletAddress, isConnected, chainId]);

    // ---------- Filtered rows for current tab + search ----------
    const visibleRows = useMemo(() => {
        const rows =
            activeTab === 'milestone'
                ? trackedPaymentRequestData
                : directPaymentRequestData;
        const q = searchTerm.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const idLabel = `REQ-${String(r.id).padStart(3, '0')}`.toLowerCase();
            return (
                idLabel.includes(q) ||
                String(r.id).includes(q) ||
                (r.receiver || '').toLowerCase().includes(q) ||
                (r.description || '').toLowerCase().includes(q) ||
                (r.tokenName || '').toLowerCase().includes(q)
            );
        });
    }, [
        activeTab,
        directPaymentRequestData,
        trackedPaymentRequestData,
        searchTerm,
    ]);

    const handleOpenForm = () => {
        setShowCreateForm(true);
        setTimeout(() => {
            formAnchorRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 50);
    };

    const handleCloseForm = () => setShowCreateForm(false);

    return (
        <div className="crypto-page relative min-h-[calc(100vh-80px)]">
            <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                {!isConnected ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                        <ConnectWalletButton />
                    </div>
                ) : chainId !== targetChainId ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                        <SwitchNetworkButton />
                    </div>
                ) : !isBackendReady ? (
                    <BackendWalletSignInPanel
                        walletAddress={walletAddress}
                        status={authStatus}
                        error={authError}
                        onRetry={signIn}
                        title="Register to request payments"
                        description="Sign a free message to create your CronPay account before sending payment requests."
                    />
                ) : (
                    <div className="animate-fade-in space-y-6">
                        {/* HEADER */}
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-teal-200 bg-teal-50 dark:border-teal-900/50 dark:bg-teal-950/40">
                                    <i className={`${receiveIcon} rotate-45 text-teal-700 dark:text-teal-400`} />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 md:text-3xl dark:text-white">
                                        Get paid
                                    </h1>
                                    <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-400">
                                        Request payments from others
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={
                                    showCreateForm ? handleCloseForm : handleOpenForm
                                }
                                className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                            >
                                <i
                                    className={`fa-solid ${showCreateForm ? 'fa-xmark' : 'fa-plus'
                                        } text-xs`}
                                />
                                {showCreateForm ? 'Close' : 'New Request'}
                            </button>
                        </div>

                        {/* INLINE CREATE FORM */}
                        <div ref={formAnchorRef}>
                            {showCreateForm && (
                                <SendRequest
                                    onCancel={handleCloseForm}
                                    onSuccess={handleCloseForm}
                                    getDirectPaymentRequestData={
                                        getDirectPaymentRequestData
                                    }
                                    getLockedPaymentRequestData={
                                        getLockedPaymentRequestData
                                    }
                                />
                            )}
                        </div>

                        {/* REQUESTS TABLE */}
                        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none md:rounded-2xl">
                            <div className="flex flex-col gap-3 border-b border-zinc-200 bg-zinc-50/80 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/30 sm:flex-row sm:items-center sm:justify-between">
                                <div className="inline-flex items-center gap-0.5 rounded-lg border border-zinc-200 bg-white p-0.5 dark:border-zinc-700 dark:bg-zinc-900/80">
                                    {TABS.map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${activeTab === tab.id
                                                    ? 'bg-teal-700 text-white shadow-sm dark:bg-teal-600 dark:text-white'
                                                    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'
                                                }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="relative w-full sm:w-72">
                                    {/* <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search requests..."
                                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-950/60 border border-slate-800 text-slate-200 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/40"
                                    /> */}
                                </div>
                            </div>

                            {/* Header row */}
                            <div className="hidden border-b border-zinc-200 bg-zinc-50/90 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-500 sm:grid sm:grid-cols-12 sm:gap-3">
                                <div className="col-span-2">Request ID</div>
                                <div className="col-span-2">To</div>
                                <div className="col-span-2">Amount</div>
                                <div className="col-span-3">Description</div>
                                <div className="col-span-2">Date</div>
                                <div className="col-span-1 text-right">Status</div>
                            </div>

                            {/* Body */}
                            {loading && visibleRows.length === 0 ? (
                                <TableSkeleton rows={3} />
                            ) : visibleRows.length === 0 ? (
                                <EmptyState
                                    tab={activeTab}
                                    onCreate={handleOpenForm}
                                />
                            ) : (
                                <div>
                                    {visibleRows.map((row) => (
                                        <RequestRow
                                            key={`${activeTab}-${row.id}`}
                                            row={row}
                                            isMilestone={activeTab === 'milestone'}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {disputeModal && (
                <DisputeModal
                    setDisputeModal={setDisputeModal}
                    currentItem={currentItem}
                    setCurrentItem={setCurrentItem}
                />
            )}
        </div>
    );
};

export default GetPaid;
